import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StoryboardStatusBar, ViewMode } from './StoryboardStatusBar';
import { StripCard } from './StripCard';

import type { Storyboard, StoryboardPanel } from '@/lib/storyboard-types';
import type { Scene } from '@/lib/types';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`strip-tabpanel-${index}`}
      aria-labelledby={`strip-tab-${index}`}
      style={{ height: '100%', overflow: 'auto' }}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `strip-tab-${index}`,
    'aria-controls': `strip-tabpanel-${index}`,
  };
}

interface StoryboardEditorProps {
  storyboard: Storyboard | null;
  selectedScene: Scene | null;
  isGenerating: boolean;
  onUpdatePanel: (stripId: string, panelId: string, updates: Partial<StoryboardPanel>) => void;
  onBatchUpdatePanels: (stripId: string, updates: { panelId: string; updates: Partial<StoryboardPanel> }[]) => void;
  onRegeneratePanel: (stripId: string, panelId: string, changeRequest?: string) => void;
  // Image generation
  onGeneratePanelImage?: (stripId: string, panelId: string) => void;
  onRefinePanelImage?: (stripId: string, panelId: string, changeRequest: string) => void;
  onAcceptPanelImage?: (stripId: string, panelId: string, iterationIndex: number) => void;
  isGeneratingImage?: (panelId: string) => boolean;
  isTokenDepleted?: boolean;
}

export function StoryboardEditor({
  storyboard,
  selectedScene,
  isGenerating,
  onUpdatePanel,
  onBatchUpdatePanels,
  onRegeneratePanel,
  onGeneratePanelImage,
  onRefinePanelImage,
  onAcceptPanelImage,
  isGeneratingImage,
  isTokenDepleted = false,
}: StoryboardEditorProps) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('tabs');
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Empty state - no scene selected
  if (!selectedScene) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
            {t('storyboard.editor.select_scene_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('storyboard.editor.select_scene_desc')}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Generating state
  if (isGenerating) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {t('storyboard.editor.generating_script')}
        </Typography>
        <Box sx={{ width: 300 }}>
          <LinearProgress />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t('storyboard.editor.generating_script_desc')}
        </Typography>
      </Box>
    );
  }

  // No storyboard yet
  if (!storyboard) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
            {t('storyboard.editor.ready_to_generate_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('storyboard.editor.ready_to_generate_desc', { sceneName: selectedScene.name })}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Get panel counts per strip for tab labels
  const getStripLabel = (strip: typeof storyboard.strips[0]) => {
    const accepted = strip.panels.filter((p) => p.scriptStatus === 'accepted').length;
    const total = strip.panels.length;
    return t('storyboard.editor.strip_label', { order: strip.order, accepted, total });
  };

  // Storyboard editor
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Status Bar with View Mode Toggle */}
      <StoryboardStatusBar
        storyboard={storyboard}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Tabs Mode - MUI Tabs for strips */}
      {viewMode === 'tabs' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="strips tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {storyboard.strips.map((strip, index) => (
                <Tab
                  key={strip.id}
                  label={getStripLabel(strip)}
                  {...a11yProps(index)}
                  sx={{
                    fontWeight: 600,
                    '&.Mui-selected': {
                      color: strip.panels.every((p) => p.scriptStatus === 'accepted')
                        ? 'success.main'
                        : 'primary.main',
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
          {storyboard.strips.map((strip, index) => (
            <TabPanel key={strip.id} value={activeTab} index={index}>
              <StripCard
                strip={strip}
                isScrollMode={true}
                onUpdatePanel={(panelId: string, updates: Partial<StoryboardPanel>) =>
                  onUpdatePanel(strip.id, panelId, updates)
                }
                onBatchUpdatePanels={(updates: { panelId: string; updates: Partial<StoryboardPanel> }[]) =>
                  onBatchUpdatePanels(strip.id, updates)
                }
                onRegeneratePanel={(panelId: string, changeRequest?: string) =>
                  onRegeneratePanel(strip.id, panelId, changeRequest)
                }
                onGeneratePanelImage={onGeneratePanelImage ? (panelId: string) => onGeneratePanelImage(strip.id, panelId) : undefined}
                onRefinePanelImage={onRefinePanelImage ? (panelId: string, cr: string) => onRefinePanelImage(strip.id, panelId, cr) : undefined}
                onAcceptPanelImage={onAcceptPanelImage ? (panelId: string, idx: number) => onAcceptPanelImage(strip.id, panelId, idx) : undefined}
                isGeneratingImage={isGeneratingImage}
                isTokenDepleted={isTokenDepleted}
              />
            </TabPanel>
          ))}
        </Box>
      )}

      {/* Scroll Mode - All strips in continuous vertical flow */}
      {viewMode === 'scroll' && (
        <Box sx={{ flex: 1, overflow: 'auto', py: 2 }}>
          {storyboard.strips.map((strip) => (
            <StripCard
              key={strip.id}
              strip={strip}
              isScrollMode={true}
              onUpdatePanel={(panelId: string, updates: Partial<StoryboardPanel>) =>
                onUpdatePanel(strip.id, panelId, updates)
              }
              onBatchUpdatePanels={(updates: { panelId: string; updates: Partial<StoryboardPanel> }[]) =>
                onBatchUpdatePanels(strip.id, updates)
              }
              onRegeneratePanel={(panelId: string, changeRequest?: string) =>
                onRegeneratePanel(strip.id, panelId, changeRequest)
              }
              onGeneratePanelImage={onGeneratePanelImage ? (panelId: string) => onGeneratePanelImage(strip.id, panelId) : undefined}
              onRefinePanelImage={onRefinePanelImage ? (panelId: string, cr: string) => onRefinePanelImage(strip.id, panelId, cr) : undefined}
              onAcceptPanelImage={onAcceptPanelImage ? (panelId: string, idx: number) => onAcceptPanelImage(strip.id, panelId, idx) : undefined}
              isGeneratingImage={isGeneratingImage}
              isTokenDepleted={isTokenDepleted}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
